import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../components/about/AboutSideNav';

// ===== English mirror of the "About" section entry hub (Implementer B scope). =====
// Same section grammar as src/pages/about/page.tsx (JA). Title/description contain no Japanese (SEO rule 1-3).
// Facts are sourced from canon only (see JA page header for source paths); no invented years/figures.

export default function AboutPageEN() {
  useEffect(() => {
    document.title = 'About Us | Japan Butler Association';
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en/' },
    { label: 'About Us' }
  ];

  const aboutNav = [
    { label: 'About Us', href: '/en/about' },
    { label: 'Overview', href: '/en/about/overview' },
    { label: 'Mission', href: '/en/about/mission' },
    { label: 'History', href: '/en/about/history' },
    { label: 'Organization', href: '/en/about/organization' },
    { label: 'Activities', href: '/en/about/activities' },
    { label: 'Access', href: '/en/about/access' }
  ];

  const sections = [
    { no: '01', en: 'Overview', ja: 'Overview', href: '/en/about/overview', desc: 'The origins and full picture of the association, including its corporate profile and five areas of activity.' },
    { no: '02', en: 'Mission', ja: 'Mission', href: '/en/about/mission', desc: 'Our philosophy — the fusion of Japanese hospitality with Western butler service — and the value we deliver.' },
    { no: '03', en: 'History', ja: 'History', href: '/en/about/history', desc: 'From our founding in 1890 (Meiji 23) to today: three generations of inheritance and progress.' },
    { no: '04', en: 'Organization', ja: 'Organization', href: '/en/about/organization', desc: 'The structure of the association led by its president, and the framework that upholds quality and trust.' },
    { no: '05', en: 'Activities', ja: 'Activities', href: '/en/about/activities', desc: 'Our pillars of activity: training, certification, seminars, international exchange, and publishing.' },
    { no: '06', en: 'Access', ja: 'Access', href: '/en/about/access', desc: 'The location of our headquarters in Aomi, Odaiba, and directions from the nearest stations.' },
  ];

  const principles = [
    { key: 'i.', title: 'Time and Peace of Mind', text: 'We free you from logistics and chores so you can focus on what truly matters — work, family, and rest.' },
    { key: 'ii.', title: 'The Heart of the Home', text: 'Entering the innermost circle of daily life, we quietly maintain the foundations of the household and attend to its subtleties.' },
    { key: 'iii.', title: 'Unseen Service', text: 'Anticipating needs before they are spoken and moving as naturally as air — this anticipatory service is the essence of a butler.' },
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  return (
    <Layout>
      <title>About Us | Japan Butler Association</title>
      <meta name="description" content="Learn about Japan Butler Association, founded in 1890. Explore our overview, history, organization, activities, and access — your gateway to Japan's only dedicated butler institution with over 130 years of heritage." />
      <link rel="canonical" href="https://butler-association.jp/en/about" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/about" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/about" />
      <link rel="alternate" hrefLang="x-default" href="https://butler-association.jp/about" />

      {/* Hero — full-bleed billboard */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/salon0926/1786973212294.jpg"
          alt="The butler salon's silver tea set with the fireplace behind it"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">About the Association</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">Carrying Butler Culture</span>
              <span className="block">into the Future</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                Since its founding in 1890 (Meiji 23), Japan Butler Association has devoted itself to promoting and developing butler culture in Japan.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                By blending the spirit of Omotenashi with the Western butler tradition, we cultivate professionals of world-class standing. This page introduces the association through six perspectives.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Body = side navigation + content, two columns */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            <AboutSideNav items={aboutNav} activeHref="/en/about" eyebrow="About Us" heading="About Us" />

            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* I. Who We Are */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Who We Are</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  Japan's Only <span className="text-[#a38e65]">Dedicated Butler Institution</span>
                </h2>
                <div className="grid lg:grid-cols-[1fr_300px] gap-8 lg:gap-12 items-start">
                  <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                    <p>
                      Japan Butler Association is the country's only dedicated butler institution, with a lineage spanning more than 130 years. Since its founding in 1890 (Meiji 23), we have devoted ourselves to promoting and developing butler culture in Japan.
                    </p>
                    <div className="space-y-5">
                      <p>
                        What we champion is the fusion of the Japanese spirit of hospitality with Western butler service.
                      </p>
                      <p>
                        Professionals who combine British discipline and refinement with Japan's own attentive courtesy support our clients comprehensively — from private life to business.
                      </p>
                    </div>
                    <p>
                      This page is your gateway to the association. Through six perspectives — overview, mission, history, organization, activities, and location — you can see who we are.
                    </p>
                    <p>
                      Each page tells more of how the association carries butler culture into the next era. Begin with whichever perspective interests you most.
                    </p>
                  </div>
                  <div className="lg:pt-1">
                    <img
                      src="/images/generated/jba-fs-020-about-v2.webp"
                      alt="A tranquil reception scene symbolizing the philosophy of butler culture the association upholds"
                      className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
                    />
                  </div>
                </div>
              </section>

              {/* II. Explore — subpage directory (new variation) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Explore</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  The Association from <span className="text-[#a38e65]">Six Perspectives</span>
                </h2>
                <div className="space-y-5 mb-9">
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    We have organized the gateway to the association into six pages by purpose. If you want the full picture first, start with the Overview; if you want to understand how we think, start with our Mission.
                  </p>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    Choose the perspective that interests you and continue to each page.
                  </p>
                </div>
                <div className="border-t-2 border-[#1a2942]">
                  {sections.map((s) => (
                    <a
                      key={s.href}
                      href={s.href}
                      className="group flex items-center gap-5 sm:gap-8 py-6 sm:py-7 border-b border-[#e2ddd2] hover:bg-[#f8f6f0]/60 transition-colors duration-300"
                    >
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl sm:text-4xl leading-none shrink-0 w-12 sm:w-14">{s.no}</span>
                      <div className="min-w-0 flex-1">
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{s.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-[22px] sm:text-2xl leading-[1.4] tracking-[0.02em] mb-1.5 group-hover:text-[#8f7a56] transition-colors">{s.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{s.desc}</p>
                      </div>
                      <span className="text-[#a38e65] text-xl sm:text-2xl shrink-0 w-6 text-right group-hover:translate-x-1 transition-transform">→</span>
                    </a>
                  ))}
                </div>
              </section>

              {/* III. Our Principles — split editorial */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Our Principles</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  What We Hold Dear
                </h2>
                <div className="grid lg:grid-cols-2 items-stretch border border-[#e2ddd2] overflow-hidden">
                  <div className="relative min-h-[280px] lg:min-h-[460px] bg-black order-1">
                    <img
                      src="/images/generated/jba-p-009-about-welcome-w3.webp"
                      alt="A butler quietly receiving a guest — anticipatory hospitality embodied in service"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                  <div className="order-2 flex flex-col justify-center px-7 sm:px-10 lg:px-12 py-10 sm:py-12 lg:py-14 text-left">
                    <div className="space-y-5 mb-8">
                      <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                        What we provide is not a substitute for labor. It is the very environment in which you are freed from logistics and chores to focus on what truly matters.
                      </p>
                      <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                        More than housekeeping, we quietly maintain the foundations of daily life and support you by anticipating what comes next — the essence of the butler's calling, held in three principles.
                      </p>
                    </div>
                    <dl className="border-t border-[#e2ddd2]">
                      {principles.map((p) => (
                        <div key={p.key} className="border-b border-[#e2ddd2] py-4">
                          <dt className="flex items-baseline gap-3 mb-1.5">
                            <span className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em]">{p.key}</span>
                            <span className="jba-display font-medium text-[#1a2942] text-lg leading-[1.4]">{p.title}</span>
                          </dt>
                          <dd className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              </section>

              {/* IV. Next Steps — navy CTA panel */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Discover More of the Association
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <div className="space-y-5 mb-8">
                    <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                      Japan Butler Association takes root in society through butler training and certification, practical seminars, and the operation of the butler staffing service "THE BUTLER."
                    </p>
                    <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                      Once you have looked through our philosophy and activities, our certification programs, service lineup, and contact desk are here to help.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/en/qualifications" className={BTN08_ON_NAVY}>Certification Programs</a>
                    <a href="/en/butler-services/" className={BTN08_ON_NAVY}>Butler Services</a>
                    <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
